import React, { useState, useMemo } from 'react';

// Generate a wobbly rectangle path that looks hand-drawn
function generateWobblyRect(w, h, seed = 0) {
  const jitter = (base, amount) => base + Math.sin(seed + base) * amount;
  const j = 3;
  return `
    M ${jitter(2, j)},${jitter(2, j)}
    L ${jitter(w - 2, j)},${jitter(3, j)}
    L ${jitter(w - 3, j)},${jitter(h - 2, j)}
    L ${jitter(3, j)},${jitter(h - 3, j)}
    Z
  `;
}

export default function ScribbleButton({ children, onClick, href, variant = 'primary', style = {} }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const seed = useMemo(() => Math.random() * 100, []);
  const tilt = useMemo(() => (Math.random() - 0.5) * 4, []);

  const isPrimary = variant === 'primary';
  const isX = variant === 'x';

  const baseStyles = {
    position: 'relative',
    display: 'inline-block',
    padding: '14px 32px',
    fontFamily: "'Permanent Marker', cursive",
    fontSize: isPrimary ? '22px' : '18px',
    color: isPrimary || isX ? '#f5f3ee' : '#1a1a1a',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transform: `rotate(${tilt}deg) scale(${isPressed ? 0.94 : isHovered ? 1.06 : 1})`,
    transition: 'transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)',
    textDecoration: 'none',
    zIndex: 1,
    ...style,
  };

  const svgFill = isPrimary
    ? '#1a1a1a'
    : isX
    ? '#1a1a1a'
    : 'none';

  const svgStroke = '#1a1a1a';

  const content = (
    <>
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
        }}
        viewBox="0 0 200 60"
        preserveAspectRatio="none"
      >
        <path
          d={generateWobblyRect(200, 60, seed)}
          fill={svgFill}
          stroke={svgStroke}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Extra scribble line on hover */}
        {isHovered && (
          <path
            d={generateWobblyRect(196, 56, seed + 50)}
            fill="none"
            stroke={isPrimary ? '#f5f3ee' : '#1a1a1a'}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="4 3"
            opacity="0.5"
          />
        )}
      </svg>
      <span style={{ position: 'relative', zIndex: 2, whiteSpace: 'nowrap' }}>
        {children}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={baseStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      style={baseStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      {content}
    </button>
  );
}
